/*
 * Bundle CTA for product cards.
 *
 * A Shopify native bundle must send the shopper to the PDP - its components are chosen there, so
 * adding it straight to the bag from a card skips the whole point of the product. The card still
 * shows the price; only the CTA changes to "View Set" and becomes a link.
 *
 * Nothing in the Liquid product object says "this is a bundle". product.type is no help: the store
 * types both real bundles and 20 legacy hand-built sets as "Sets", and options_with_values is empty
 * on a bundle whose components are all locked to one variant (The Super Setters). The only reliable
 * flag is ProductVariant.requiresComponents on the Storefront API, which Liquid cannot read.
 *
 * So there are two sources, same as the PDP's fixed-component cards:
 *
 *   1. The custom.is_bundle metafield, rendered server-side as data-is-bundle. Authoritative and
 *      flash-free - the card paints "View Set" on the first frame. Needs setting per bundle.
 *   2. This script, which batches a requiresComponents lookup for every card that arrived without
 *      a verdict. Self-healing for bundles nobody flagged, at the cost of a brief label swap.
 *
 * Cards with data-is-bundle set either way are never queried, so once the metafield is populated
 * the request disappears on most pages.
 */
(function () {
  const STOREFRONT_ACCESS_TOKEN = 'cf1d4540fe597eef6bf5332e8217e873';
  const STOREFRONT_API_VERSION = '2025-07';
  const BUNDLE_CTA_TEXT = 'View Set';
  const CACHE_KEY = 'wc:bundle-variants';

  // Batched on purpose: a filtered collection page can paint 24+ cards at once, and one
  // nodes(ids:) call costs a point per id rather than a round trip per card.
  const REQUIRES_COMPONENTS_QUERY = `
    query CardBundleFlags($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          requiresComponents
        }
      }
    }
  `;

  // Attributes that make a card's button add to the bag. A bundle keeps none of them - leaving
  // data-drawer-trigger behind would open the mini-cart on top of the PDP navigation.
  const ADD_TO_CART_ATTRIBUTES = [
    'data-selected-variant',
    'data-drawer-trigger',
    'aria-controls',
    'aria-expanded',
  ];

  // sessionStorage only. The verdict is a property of the product, so it survives navigation, but
  // it is not worth persisting past the tab - a merchant can change a bundle's components.
  const readCache = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(CACHE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };

  const writeCache = (cache) => {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      /* Private mode, or the quota is full. The lookup just runs again next page. */
    }
  };

  // Everything keys off the CTA element rather than a card wrapper: the four renderers disagree
  // about the wrapper (<wc-product-card>, .card-wrapper, Boost's own markup) but they all carry
  // the same data-bundle-cta hook.
  const getCardRoot = (cta) => cta.closest('wc-product-card, .card-wrapper') || cta.parentElement;

  // The variant the card would add to the bag. Falls back to the card's product JSON, which is the
  // only source on a card whose CTA links out (Select Option) and therefore has no selected variant.
  const getCardVariantId = (cta) => {
    const selected = cta.getAttribute('data-selected-variant');
    if (selected) return selected;

    const fromAttribute = cta.getAttribute('data-card-variant-id');
    if (fromAttribute) return fromAttribute;

    try {
      const json = JSON.parse(
        getCardRoot(cta)?.querySelector('[data-product-json]')?.textContent || '{}'
      );
      const variantId = json.variants?.[0]?.id;
      return variantId ? String(variantId) : null;
    } catch (error) {
      return null;
    }
  };

  const getProductUrl = (cta) => {
    const card = getCardRoot(cta);
    return (
      cta.getAttribute('data-product-url') ||
      card?.getAttribute('data-product-url') ||
      card?.querySelector('[data-card-product-link]')?.getAttribute('href') ||
      null
    );
  };

  // A bundle's options are its component choices, which only read as choices inside the PDP's
  // bundle builder next to each component's image and title. On a card they are bare shade
  // descriptions, and with a "View Set" CTA there is no variant for a pill to select anyway.
  //
  // Both server renderers already omit the row when custom.is_bundle says so. This is for the
  // other path: a bundle resolved at runtime from requiresComponents was rendered as an ordinary
  // product, so its pills are already on the page and have to be taken back off.
  const removeOptionRow = (cta) => {
    const card = getCardRoot(cta);
    if (!card) return;

    // Last of the three places this class is set - the two server renderers get it into the
    // markup on the first frame, and this covers the bundle neither of them could identify,
    // which is the only case where swatches were ever on screen to begin with.
    (card.closest('wc-product-card') || card).classList.add('wc-product-card--bundle');

    card
      .querySelectorAll(
        '.featured-product-card__option-track, .featured-product-card__option-nav'
      )
      .forEach((node) => node.remove());

    // Emptied rather than removed: featured-product-card.js looks this up by attribute on every
    // swatch interaction, and a bundle card can still get one from a stray rebind.
    const selectedLabel = card.querySelector('[data-selected-color-name]');
    if (selectedLabel) selectedLabel.textContent = '';

    // The wrapper carries its own border-top, margin and padding, so an empty one still draws a
    // rule and a gap where the swatches were. Tagged here rather than in each renderer because
    // this runs for every bundle card on every renderer - the metafield path calls applyBundleCta
    // too, not just the requiresComponents backfill - and Boost's card wrapper is a fixed
    // template that cannot take a conditional class.
    const optionSelector = card.querySelector('.option-selector');
    if (optionSelector) optionSelector.classList.add('option-selector--bundle');
  };

  const applyBundleCta = (cta) => {
    if (cta.dataset.bundleCtaApplied === 'true') return;

    const url = getProductUrl(cta);
    if (!url) return;

    cta.dataset.bundleCtaApplied = 'true';
    cta.dataset.isBundle = 'true';
    removeOptionRow(cta);

    // Out of stock wins, exactly as it does for a Kit in product-card.liquid. Rewriting an
    // "Out of Stock" button into a live link would make an unbuyable bundle look buyable.
    if (cta.hasAttribute('outofstock')) return;

    ADD_TO_CART_ATTRIBUTES.forEach((attribute) => cta.removeAttribute(attribute));
    cta.classList.remove('js-open-selectors');

    const label = cta.querySelector('.button-text');
    if (label) label.textContent = BUNDLE_CTA_TEXT;

    // The price markup is deliberately left alone - a bundle card still shows $55 $70.
    // Only wrap if the button is not already a link (multi-option cards render their own <a>).
    if (!cta.querySelector('a')) {
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('aria-label', BUNDLE_CTA_TEXT);
      while (cta.firstChild) link.appendChild(cta.firstChild);
      cta.appendChild(link);
    } else {
      cta.querySelector('a').setAttribute('href', url);
    }
  };

  const fetchBundleFlags = async (variantIds) => {
    const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: REQUIRES_COMPONENTS_QUERY,
        variables: { ids: variantIds.map((id) => `gid://shopify/ProductVariant/${id}`) },
      }),
    });

    if (!response.ok) {
      throw new Error(`Bundle flag request failed with status ${response.status}`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join(', '));
    }

    const flags = {};
    (payload.data?.nodes || []).forEach((node) => {
      if (!node?.id) return;
      flags[node.id.split('/').pop()] = Boolean(node.requiresComponents);
    });
    return flags;
  };

  let pending = false;

  const resolveCards = async () => {
    if (pending) return;

    const ctas = Array.from(
      document.querySelectorAll('[data-bundle-cta]:not([data-bundle-resolved])')
    );
    if (!ctas.length) return;

    const cache = readCache();
    const unknown = [];

    ctas.forEach((cta) => {
      cta.dataset.bundleResolved = 'true';

      // Server-side verdict from the custom.is_bundle metafield - no request needed either way.
      if (cta.dataset.isBundle === 'true') {
        applyBundleCta(cta);
        return;
      }
      if (cta.dataset.isBundle === 'false') return;

      const variantId = getCardVariantId(cta);
      if (!variantId) return;

      if (variantId in cache) {
        if (cache[variantId]) applyBundleCta(cta);
        return;
      }

      unknown.push({ cta, variantId });
    });

    if (!unknown.length) return;

    pending = true;
    try {
      const flags = await fetchBundleFlags([...new Set(unknown.map((entry) => entry.variantId))]);
      Object.assign(cache, flags);
      writeCache(cache);
      unknown.forEach(({ cta, variantId }) => {
        if (flags[variantId]) applyBundleCta(cta);
      });
    } catch (error) {
      // A failed lookup leaves the card exactly as Liquid rendered it. Add to Bag on a bundle
      // still works - the shopper just skips the component picker - so this must not throw.
      console.error('Unable to resolve bundle product cards:', error);
      unknown.forEach(({ cta }) => delete cta.dataset.bundleResolved);
    } finally {
      pending = false;
    }
  };

  // Boost renders the whole collection grid client-side and the carousels clone slides, so cards
  // keep arriving after DOMContentLoaded. Debounced because a Boost render inserts them in bulk.
  let scheduled = null;
  const scheduleResolve = () => {
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(resolveCards, 100);
  };

  const start = () => {
    resolveCards();
    new MutationObserver((mutations) => {
      const addedElements = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => node.nodeType === 1)
      );
      if (addedElements) scheduleResolve();
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
